// src/modules/home/components/PublishExperimentModal.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Modal for publishing / updating a published experiment.
// Props:
//   snapshot    — lightweight object describing experiment results (no raw data)
//   expType     — "auto_eda" | "ml_experiments" | "dl_experiments" | "doc_analysis"
//   existingId  — if editing an already-published experiment
//   onClose()
//   onPublished(experiment) — called with the returned experiment doc
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { API_BASE_URL, experiments as expApi } from '../../api/client';
import {
  buildPublicReportAppPath,
  buildPublicReportAppUrl,
  buildPublicReportHtmlUrl,
} from '../../utils/publicReports';

const C = {
  void:    '#020508',
  deep:    '#040c14',
  surface: '#071220',
  raised:  '#0a1929',
  b0:      '#0d2137',
  b1:      '#12304f',
  b2:      '#1a4266',
  t1:      '#f0f8ff',
  t2:      '#9bbcd4',
  t3:      '#5a7f9a',
  t4:      '#334d5c',
  cyan:    '#22d3ee',
  violet:  '#a78bfa',
  green:   '#34d399',
  orange:  '#fb923c',
  gold:    '#fbbf24',
  red:     '#f87171',
  pink:    '#f472b6',
};

const TYPE_LABELS = {
  auto_eda:        { label: 'Auto EDA',          icon: '◉', color: C.cyan   },
  ml_experiments:  { label: 'ML Experiment',     icon: '⚡', color: C.orange },
  dl_experiments:  { label: 'DL Experiment',     icon: '🧠', color: C.green  },
  doc_analysis:    { label: 'Document Analysis', icon: '◈', color: C.violet },
  image_insights:  { label: 'Image Insights',    icon: '🖼', color: C.pink   },
  audio_video:     { label: 'Audio / Video',     icon: '🎙', color: C.gold   },
};

const VIS_OPTIONS = [
  { id: 'public',     label: 'Public',     desc: 'Anyone can discover this analysis', icon: '🌐', color: C.green  },
  { id: 'blockchain', label: 'Blockchain', desc: 'Permanently recorded on-chain',       icon: '⛓',  color: C.violet },
];

function SnapshotPreview({ snapshot, expType }) {
  if (!snapshot) return null;
  const ds = snapshot.dataset;
  const metrics = snapshot.metrics || {};
  const insights = (snapshot.insights || []).slice(0, 4);
  const tabs = snapshot.tabs_completed || [];
  const typeMeta = TYPE_LABELS[expType] || { label: expType, icon: '⬟', color: C.cyan };

  return (
    <div style={{ background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 5, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', fontWeight: 700 }}>EXPERIMENT SNAPSHOT</div>

      {ds && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, padding: '3px 8px', background: `${typeMeta.color}18`, border: `1px solid ${typeMeta.color}40`, borderRadius: 2, color: typeMeta.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>
            {typeMeta.icon} {typeMeta.label}
          </span>
          {ds.name && (
            <span style={{ fontSize: 9, padding: '3px 8px', background: `${C.cyan}12`, border: `1px solid ${C.cyan}30`, borderRadius: 2, color: C.t2, fontFamily: 'JetBrains Mono,monospace' }}>
              📊 {ds.name}
            </span>
          )}
          {ds.rows > 0 && (
            <span style={{ fontSize: 9, padding: '3px 8px', background: `${C.b1}`, borderRadius: 2, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>
              {ds.rows.toLocaleString()} rows × {ds.cols} cols
            </span>
          )}
          {snapshot.quality_score != null && (
            <span style={{ fontSize: 9, padding: '3px 8px', background: snapshot.quality_score >= 80 ? `${C.green}18` : `${C.gold}18`, border: `1px solid ${snapshot.quality_score >= 80 ? C.green : C.gold}40`, borderRadius: 2, color: snapshot.quality_score >= 80 ? C.green : C.gold, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>
              ✦ Quality {snapshot.quality_score}/100
            </span>
          )}
        </div>
      )}

      {Object.keys(metrics).length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(metrics).slice(0, 6).map(([k, v]) => (
            <div key={k} style={{ padding: '4px 9px', background: C.raised, border: `1px solid ${C.b2}`, borderRadius: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase' }}>{k.replace(/_/g, ' ')}</span>
              <span style={{ fontSize: 9, color: C.t1, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {insights.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {insights.map((ins, i) => (
            <div key={i} style={{ fontSize: 10, color: C.t2, paddingLeft: 10, borderLeft: `2px solid ${C.cyan}44`, lineHeight: 1.6 }}>
              {ins}
            </div>
          ))}
        </div>
      )}

      {tabs.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', paddingTop: 2 }}>
          <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', marginRight: 2 }}>TABS:</span>
          {tabs.map(t => (
            <span key={t} style={{ fontSize: 8, padding: '2px 6px', background: `${C.violet}14`, border: `1px solid ${C.violet}30`, borderRadius: 2, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublishExperimentModal({ snapshot, expType, existingId, onClose, onPublished }) {
  const typeMeta = TYPE_LABELS[expType] || { label: expType, icon: '⬟', color: C.cyan };

  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput,   setTagsInput]   = useState('');
  const [visibility,  setVisibility]  = useState('public');
  const [consent,     setConsent]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState(false);
  const [publishedExp, setPublishedExp] = useState(null);
  const [copyState, setCopyState] = useState('');
  const [copyHtmlState, setCopyHtmlState] = useState('');

  const publicPath = buildPublicReportAppPath(publishedExp?.id, publishedExp?.slug || publishedExp?.title || '');
  const publicUrl = buildPublicReportAppUrl(publishedExp?.id, publishedExp?.slug || publishedExp?.title || '');
  const htmlUrl = publishedExp?.html_url || buildPublicReportHtmlUrl(publishedExp?.id, API_BASE_URL);

  const finishClose = useCallback(() => {
    if (publishedExp) onPublished?.(publishedExp);
    onClose?.();
  }, [publishedExp, onPublished, onClose]);

  const handlePublish = useCallback(async () => {
    if (visibility === 'blockchain') return; // blocked — coming soon
    if (!title.trim()) { setError('Title is required'); return; }
    if (!consent) { setError('Please acknowledge the data sharing notice'); return; }
    setError('');
    setLoading(true);
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean).slice(0, 5);
      const payload = {
        type: expType,
        title: title.trim(),
        description: description.trim(),
        tags,
        visibility,
        snapshot: snapshot || {},
        ...(existingId ? { id: existingId } : {}),
      };
      const data = await expApi.publish(payload);
      setPublishedExp(data.experiment || null);
      setSuccess(true);
    } catch (err) {
      const msg = err?.data?.error || err?.message || 'Publish failed';
      if (err?.status === 409) {
        setError(`Publish limit reached — ${msg}`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [title, description, tagsInput, visibility, consent, expType, snapshot, existingId]);

  const handleCopyLink = useCallback(async () => {
    if (!publicUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopyState('COPIED');
      window.setTimeout(() => setCopyState(''), 1600);
    } catch (_) {
      setCopyState('FAILED');
      window.setTimeout(() => setCopyState(''), 1600);
    }
  }, [publicUrl]);

  const handleCopyHtmlLink = useCallback(async () => {
    if (!htmlUrl || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(htmlUrl);
      setCopyHtmlState('COPIED');
      window.setTimeout(() => setCopyHtmlState(''), 1600);
    } catch (_) {
      setCopyHtmlState('FAILED');
      window.setTimeout(() => setCopyHtmlState(''), 1600);
    }
  }, [htmlUrl]);

  return (
    <div
      onClick={e => e.target === e.currentTarget && finishClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.90)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        style={{ background: C.surface, border: `1px solid ${C.b2}`, borderRadius: 8, width: '100%', maxWidth: 560, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,.8)', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: C.deep, borderBottom: `1px solid ${C.b0}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 8, color: C.t4, letterSpacing: '.14em', fontFamily: 'JetBrains Mono,monospace', marginBottom: 3 }}>
              {existingId ? 'UPDATE EXPERIMENT' : 'PUBLISH EXPERIMENT'}
            </div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 17, color: C.t1, display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ color: typeMeta.color }}>{typeMeta.icon}</span>
              {typeMeta.label}
            </div>
          </div>
          <button onClick={finishClose} style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '18px 18px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Snapshot preview */}
          <SnapshotPreview snapshot={snapshot} expType={expType} />

          {/* Title */}
          <div>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 6, fontWeight: 700 }}>TITLE <span style={{ color: C.red }}>*</span></div>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Give your experiment a clear title…"
              style={{ width: '100%', background: C.deep, border: `1px solid ${title ? C.b2 : C.b1}`, color: C.t1, padding: '10px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 4, outline: 'none', boxSizing: 'border-box', transition: 'border-color .15s' }}
              onFocus={e => e.target.style.borderColor = C.cyan}
              onBlur={e => e.target.style.borderColor = title ? C.b2 : C.b1}
            />
            <div style={{ fontSize: 9, color: C.t4, marginTop: 3, textAlign: 'right', fontFamily: 'JetBrains Mono,monospace' }}>{title.length}/120</div>
          </div>

          {/* Description */}
          <div>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 6, fontWeight: 700 }}>DESCRIPTION</div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={2000}
              placeholder="What did you explore? What did you find? Share your methodology and key findings…"
              rows={4}
              style={{ width: '100%', background: C.deep, border: `1px solid ${C.b1}`, color: C.t1, padding: '10px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 4, outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.7 }}
              onFocus={e => e.target.style.borderColor = C.cyan}
              onBlur={e => e.target.style.borderColor = C.b1}
            />
          </div>

          {/* Tags */}
          <div>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 6, fontWeight: 700 }}>TAGS <span style={{ color: C.t4, fontWeight: 400 }}>(comma-separated, max 5)</span></div>
            <input
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="classification, iris, sklearn, regression…"
              style={{ width: '100%', background: C.deep, border: `1px solid ${C.b1}`, color: C.t1, padding: '10px 12px', fontSize: 12, fontFamily: 'inherit', borderRadius: 4, outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = C.cyan}
              onBlur={e => e.target.style.borderColor = C.b1}
            />
            {tagsInput && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                {tagsInput.split(',').map(t => t.trim()).filter(Boolean).slice(0, 5).map(tag => (
                  <span key={tag} style={{ fontSize: 9, padding: '2px 7px', background: `${C.violet}18`, border: `1px solid ${C.violet}40`, borderRadius: 2, color: C.violet, fontFamily: 'JetBrains Mono,monospace' }}>{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Data Sharing Consent */}
          <div style={{ padding: '13px 15px', background: `${C.gold}08`, border: `1px solid ${consent ? C.green : C.gold}44`, borderRadius: 5, transition: 'border-color .2s' }}>
            <div style={{ fontSize: 9, color: C.gold, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', fontWeight: 700, marginBottom: 8 }}>
              ⚠ DATA SHARING NOTICE
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 11 }}>
              {[
                'A sample of your dataset (up to 50 rows) will be stored and publicly visible.',
                'Analysis results, statistics, and column distributions will be publicly viewable.',
                'You confirm this data does not contain personal or sensitive information.',
                'You understand you can delete this publication at any time.',
              ].map((line, i) => (
                <div key={i} style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: C.t3, paddingLeft: 12, borderLeft: `2px solid ${C.gold}44`, lineHeight: 1.6 }}>
                  {line}
                </div>
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                style={{ width: 14, height: 14, accentColor: C.green, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: consent ? C.green : C.t2, fontWeight: 700, transition: 'color .15s' }}>
                I understand and consent to public storage of this dataset sample
              </span>
            </label>
          </div>

          {/* Visibility */}
          <div>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 8, fontWeight: 700 }}>VISIBILITY</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {VIS_OPTIONS.map(opt => {
                const active = visibility === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setVisibility(opt.id)}
                    style={{ padding: '12px 14px', background: active ? `${opt.color}12` : C.deep, border: `1px solid ${active ? opt.color : C.b1}`, borderRadius: 5, cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}
                  >
                    <div style={{ fontSize: 16, marginBottom: 4 }}>{opt.icon}</div>
                    <div style={{ fontSize: 11, color: active ? opt.color : C.t1, fontWeight: 700, fontFamily: 'Syne,sans-serif' }}>{opt.label}</div>
                    <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            {visibility === 'blockchain' && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: `${C.violet}12`, border: `1px solid ${C.violet}50`, borderRadius: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🧪</span>
                <div>
                  <div style={{ fontSize: 11, color: C.violet, fontWeight: 700, fontFamily: 'Syne,sans-serif' }}>This is in Beta — Coming soon!</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>Blockchain recording will let your analysis be permanently stored on-chain. Stay tuned.</div>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div style={{ padding: '9px 13px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 4, fontSize: 10, color: C.red }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{ padding: '12px 13px', background: `${C.green}12`, border: `1px solid ${C.green}40`, borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: C.green, fontWeight: 700, textAlign: 'center', marginBottom: 10 }}>
                ✓ Published successfully!
              </div>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 6, letterSpacing: '.12em' }}>
                LIVE REPORT URL
              </div>
              <a href={publicPath} style={{ color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, textDecoration: 'none', wordBreak: 'break-all', display: 'block', marginBottom: 10 }}>
                {publicUrl}
              </a>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  style={{ padding: '8px 12px', background: C.deep, border: `1px solid ${C.b1}`, color: copyState === 'COPIED' ? C.green : C.t2, borderRadius: 4, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}
                >
                  {copyState || 'COPY LINK'}
                </button>
                <a href={publicPath} style={{ padding: '8px 12px', background: `${C.cyan}18`, border: `1px solid ${C.cyan}`, color: C.cyan, borderRadius: 4, textDecoration: 'none', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}>
                  OPEN REPORT
                </a>
              </div>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', margin: '12px 0 6px', letterSpacing: '.12em' }}>
                INSTANT HTML SNAPSHOT
              </div>
              <a href={htmlUrl} target="_blank" rel="noreferrer" style={{ color: C.gold, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, textDecoration: 'none', wordBreak: 'break-all', display: 'block', marginBottom: 10 }}>
                {htmlUrl}
              </a>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={handleCopyHtmlLink}
                  style={{ padding: '8px 12px', background: C.deep, border: `1px solid ${C.b1}`, color: copyHtmlState === 'COPIED' ? C.green : C.t2, borderRadius: 4, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}
                >
                  {copyHtmlState || 'COPY HTML LINK'}
                </button>
                <a href={htmlUrl} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', background: `${C.gold}18`, border: `1px solid ${C.gold}`, color: C.gold, borderRadius: 4, textDecoration: 'none', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}>
                  OPEN HTML
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 18px', borderTop: `1px solid ${C.b0}`, flexShrink: 0, marginTop: 14 }}>
          <button
            onClick={finishClose}
            style={{ padding: '9px 18px', background: 'none', border: `1px solid ${C.b1}`, color: C.t3, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}
          >
            {success ? 'CLOSE' : 'CANCEL'}
          </button>
          {!success && (
            <button
              onClick={handlePublish}
              disabled={loading || visibility === 'blockchain' || !consent}
              style={{ padding: '9px 22px', background: `${C.cyan}18`, border: `1px solid ${C.cyan}`, color: C.cyan, borderRadius: 3, cursor: loading || !consent ? 'not-allowed' : 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, opacity: (loading || !consent) ? 0.5 : 1, transition: 'all .15s' }}
            >
              {loading ? 'PUBLISHING…' : existingId ? 'UPDATE' : visibility === 'blockchain' ? '⛓ COMING SOON' : '🌐 PUBLISH PUBLIC'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
